import { useCallback, useRef, useEffect } from 'react'
import { pipe, O, v } from '@packages/prelude'
import * as domain from '@packages/domain'
import { Configuration } from '~/store'

/**
 * Custom hook that provides navigation actions with built-in side effects
 *
 * This hook wraps the standard navigation actions and automatically handles
 * updating the window location hash when navigation occurs, with proper timing
 * to wait for accordion transitions to complete.
 */
export const useNavigationActions = (state: Configuration.State, dispatch: (action: Configuration.Action) => void) => {
  const navigationTriggerRef = useRef(false)

  // Effect that runs when currentStepId changes and navigation was triggered
  useEffect(() => {
    if (navigationTriggerRef.current) {
      pipe(
        state.currentStepId,
        O.foldMap(v.Monoid)(stepId => {
          setTimeout(() => {
            window.location.hash = `#${domain.StepId.Iso.unwrap(stepId)}`
          }, 50) // Wait for accordion transition to complete
        })
      )
      navigationTriggerRef.current = false
    }
  }, [state.currentStepId])

  const navigateNext = useCallback(() => {
    navigationTriggerRef.current = true
    dispatch(Configuration.navigateNext())
  }, [dispatch])

  const navigatePrevious = useCallback(() => {
    navigationTriggerRef.current = true
    dispatch(Configuration.navigatePrevious())
  }, [dispatch])

  return { navigateNext, navigatePrevious }
}
